import Link from 'next/link';
import { Icon } from './icon';
import { focusRing } from './focus-ring';
import { MediaThumb } from './media-thumb';
import { DietaryIndicators, type DietaryItem } from './dietary-indicators';
import styles from './related-recipe-link.module.css';

const cx = (...names: Array<string | false | null | undefined>) =>
  names.filter(Boolean).join(' ');

interface RelatedRecipeLinkProps {
  /** Recipe slug — the link resolves to `/recipes/<slug>`. */
  slug: string;
  /** The target recipe's title. Pre-translated by the caller. */
  title: string;
  /**
   * Pre-translated relationship label ("Version: vegetarian", "Based on").
   * The caller owns i18n — this component never calls a translator, matching
   * VariantChip and every other element in this directory.
   */
  eyebrow: string;
  /**
   * Accessible name for the whole link. Required rather than derived, because
   * the visible text reads as two fragments (an eyebrow above a title) and a
   * screen reader should hear one coherent sentence.
   */
  ariaLabel: string;
  /**
   * `forward` — this recipe HAS the target as a derivative. A raised card.
   * `backward` — this recipe CAME FROM the target. A recessed, quieter row.
   *
   * Each direction states itself EXACTLY once — the eyebrow glyph is the whole
   * cue, next to the words that explain it (#664).
   */
  direction?: 'forward' | 'backward';
  /** Target's hero photo. Null while its image job is still running. */
  imageUrl?: string | null;
  /**
   * Dietary glyphs for the target — usually the whole point of a derivative.
   * Pre-translated by the caller, matching how every other consumer of
   * DietaryIndicators passes them.
   */
  dietaryItems?: DietaryItem[];
  /** Accessible name for the dietary group, pre-translated. */
  dietaryLabel?: string;
  className?: string;
}

/**
 * RelatedRecipeLink — the cross-link between a recipe and a remix of it (#614),
 * restyled as a photo-led remix card (#646).
 *
 * ONE definition serving both directions. The direction is expressed through
 * props, not a second component, because the affordance is identical — only its
 * prominence differs, and a derivative deserves more of it than a back-link.
 *
 * The photograph is what does the work: it makes the row read as a DISH to go
 * and cook, rather than a settings entry. Composes MediaThumb and
 * DietaryIndicators; draws neither itself.
 *
 * Presentational and Server-Component-safe apart from MediaThumb's own polling.
 */
export function RelatedRecipeLink({
  slug,
  title,
  eyebrow,
  ariaLabel,
  direction = 'forward',
  imageUrl = null,
  dietaryItems,
  dietaryLabel,
  className,
}: RelatedRecipeLinkProps) {
  const isForward = direction === 'forward';

  return (
    <Link
      href={`/recipes/${slug}`}
      className={cx(styles.link, isForward ? styles.forward : styles.backward, focusRing, className)}
      aria-label={ariaLabel}
    >
      <MediaThumb
        imageUrl={imageUrl}
        // Decorative: the title beside it already names the dish, so alt text
        // would make a screen reader read the same recipe twice.
        alt=""
        size={isForward ? 'md' : 'sm'}
      />

      <span className={styles.body}>
        <span className={styles.eyebrow}>
          <Icon
            name={isForward ? 'layers' : 'arrow-left'}
            size={13}
            strokeWidth={2}
            aria-hidden="true"
            className={styles.eyebrowIcon}
          />
          <span className={styles.eyebrowText}>{eyebrow}</span>
        </span>
        <span className={styles.title}>{title}</span>
        {isForward && dietaryItems && dietaryItems.length > 0 && (
          <DietaryIndicators
            items={dietaryItems}
            density="card"
            label={dietaryLabel}
            className={styles.diet}
          />
        )}
      </span>

      {isForward && (
        <span className={styles.medallion}>
          <Icon name="chevron-right" size={16} strokeWidth={2} aria-hidden="true" />
        </span>
      )}
    </Link>
  );
}
